import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Tag } from '@digdir/designsystemet-react';
import type { HmsNotification } from '../../../api/safetyRepWork';
import { useMarkNotificationsReadMutation } from '../../../hooks/queries/safetyRep/useSafetyRepWork';
import { dayAndTime } from '../../WorkEnvironmentSurveyDetail/riskView';
import {
  isUnread,
  notificationAction,
  notificationLink,
  notificationText,
} from '../safetyRepWorkView';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from '../SafetyRep.module.css';

/**
 * Opening a notification marks it as read. What waits says what to do, such as «Signer
 * dokumentasjonen», and names the notification for screen readers.
 */
export const NotificationList = ({
  notifications,
}: {
  notifications: HmsNotification[];
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const markRead = useMarkNotificationsReadMutation();
  return (
    <ul className={classes.list}>
      {notifications.map((notification) => {
        const unread = isUnread(notification);
        const text = notificationText(notification, t);
        const waiting = unread && notification.kind !== 'info';
        return (
          <li
            key={notification.id}
            className={classes.notification}
            data-unread={unread}
            data-kind={notification.kind}
          >
            <span
              aria-hidden="true"
              className={classes.unreadDot}
              data-unread={unread}
              data-kind={notification.kind}
            />
            <span className={classes.notificationText}>
              <span
                className="ds-paragraph"
                data-size="sm"
                data-unread={unread}
              >
                {text}
                {unread && (
                  <span className="ds-sr-only">
                    {' '}
                    {t('safetyRep.work.notifications.unreadSr')}
                  </span>
                )}
              </span>
              <span className={classes.notificationMeta}>
                {waiting && notification.kind === 'serious' && (
                  <Tag data-size="sm" data-color="danger">
                    {t('safetyRep.work.notifications.serious')}
                  </Tag>
                )}
                <span
                  className={`ds-paragraph ${layout.subtle}`}
                  data-size="xs"
                >
                  {t('safetyRep.work.notifications.meta', {
                    team:
                      notification.survey?.team.name ??
                      t('safetyRep.work.notifications.hr'),
                    date: dayAndTime(notification.createdAt, t, i18n.language),
                  })}
                </span>
              </span>
            </span>
            <Link asChild data-size="sm">
              <RouterLink
                to={notificationLink(notification)}
                aria-label={t('safetyRep.work.notifications.openAria', {
                  action: notificationAction(notification, t),
                  text,
                })}
                className={waiting ? classes.notificationAction : undefined}
                onClick={() => unread && markRead.mutate(notification.id)}
              >
                {notificationAction(notification, t)}
              </RouterLink>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};
